/**
 * Curated rotation of public-domain classical music for Schola Games.
 *
 * Source: "250 Years of Great Music" — archive.org/details/250-years-of-great-music
 * Sorted chronologically so the shelf walks the family through music history,
 * from Baroque to mid-20th century, one piece at a time.
 *
 * Each track plays during gameplay and presents the composer + a short history
 * to the family — turning idle play into incidental music history.
 */
export type Era =
  | "medieval"
  | "renaissance"
  | "baroque"
  | "classical"
  | "romantic"
  | "modern";

export type MusicTrack = {
  id: string;
  composer: string;
  /** Composer's lifespan, e.g. "1653–1706". */
  composerYears: string;
  title: string;
  /** Year (or "c. 1680") the piece was composed. */
  composedYear: string;
  /** Numeric year used for sorting. */
  composedYearN: number;
  era: Era;
  /** A small emoji glyph used in the now-playing pill. */
  glyph: string;
  /** 2–3 short sentences kids and parents both find approachable. */
  history: string;
  /** Direct streamable MP3 URL (public domain). null if not yet sourced. */
  audioUrl: string | null;
  /** Performer / source attribution. */
  attribution?: string;
};

const ARCHIVE_BASE = "https://archive.org/download/250-years-of-great-music";
const url = (filename: string) => `${ARCHIVE_BASE}/${encodeURIComponent(filename)}`;
const ATTRIBUTION = "archive.org · 250 Years of Great Music · Public Domain";

/**
 * The full rotation. Listed roughly in chronological order of composition so
 * the shelf naturally narrates the unfolding of Western music.
 */
export const MUSIC_TRACKS: MusicTrack[] = [
  // ─── Baroque ──────────────────────────────────────────────────────────
  {
    id: "pachelbel-canon-d",
    composer: "Johann Pachelbel",
    composerYears: "1653–1706",
    title: "Canon in D",
    composedYear: "c. 1680",
    composedYearN: 1680,
    era: "baroque",
    glyph: "🎻",
    history:
      "Pachelbel was a German organist and a friend of the Bach family — he taught J.S. Bach's older brother. A canon is a musical round, like 'Row Your Boat,' where each voice enters a few beats after the last.",
    audioUrl: url("02 Canon in D.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "bach-air-g-string",
    composer: "Johann Sebastian Bach",
    composerYears: "1685–1750",
    title: "Air on the G String",
    composedYear: "c. 1730",
    composedYearN: 1730,
    era: "baroque",
    glyph: "🎻",
    history:
      "Bach wrote the Air as the slow movement of his Orchestral Suite No. 3. A 19th-century violinist arranged it so it could be played on just one string — the G string — and the gentle nickname stuck.",
    audioUrl: url("06 Air for the G String.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "handel-queen-of-sheba",
    composer: "George Frideric Handel",
    composerYears: "1685–1759",
    title: "Arrival of the Queen of Sheba",
    composedYear: "1748",
    composedYearN: 1748,
    era: "baroque",
    glyph: "👑",
    history:
      "From Handel's oratorio Solomon, this brilliant little overture pictures the legendary Queen of Sheba arriving at King Solomon's court, as told in the Old Testament. Handel was born in Germany the same year as Bach but spent most of his life in England.",
    audioUrl: url("05 Arrival of the Queen of Sheba from Solomon.mp3"),
    attribution: ATTRIBUTION,
  },

  // ─── Classical ────────────────────────────────────────────────────────
  {
    id: "haydn-serenade",
    composer: "Joseph Haydn (attrib.)",
    composerYears: "1732–1809",
    title: "Serenade",
    composedYear: "c. 1762",
    composedYearN: 1762,
    era: "classical",
    glyph: "🌙",
    history:
      "For two centuries this gentle Serenade was credited to Haydn, the elder statesman of the Classical era and Mozart's friend. Modern scholars believe it was actually written by Roman Hofstetter, a monk and admirer of Haydn — a musical mystery of mistaken identity.",
    audioUrl: url("03 Serenade.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "boccherini-minuet",
    composer: "Luigi Boccherini",
    composerYears: "1743–1805",
    title: "Minuet from String Quintet in E Major",
    composedYear: "1771",
    composedYearN: 1771,
    era: "classical",
    glyph: "💃",
    history:
      "Boccherini was an Italian cellist who spent his career at the Spanish court. This dainty minuet is one of the most famous tunes of the 18th century — light, elegant, and the perfect picture of music written for powdered wigs and candlelit rooms.",
    audioUrl: url("04 Minuet from Quintet in E Major.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "mozart-figaro-overture",
    composer: "Wolfgang Amadeus Mozart",
    composerYears: "1756–1791",
    title: "Overture to The Marriage of Figaro",
    composedYear: "1786",
    composedYearN: 1786,
    era: "classical",
    glyph: "🎭",
    history:
      "Mozart wrote his first symphony at eight and could improvise complete operas in his head. The bubbling overture to The Marriage of Figaro takes about four minutes and contains more melodies than most composers manage in an entire act.",
    audioUrl: url("01 The Marriage of Figaro Overture.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "beethoven-moonlight",
    composer: "Ludwig van Beethoven",
    composerYears: "1770–1827",
    title: "Moonlight Sonata (Op. 27 No. 2)",
    composedYear: "1801",
    composedYearN: 1801,
    era: "classical",
    glyph: "🌙",
    history:
      "Beethoven called this piece simply 'Sonata quasi una fantasia' — a sonata in the manner of a fantasy. The 'Moonlight' nickname came years later, from a critic who thought the slow first movement sounded like moonlight on Lake Lucerne. Beethoven was already losing his hearing when he wrote it.",
    audioUrl: url('13 "Moonlight" Sonata.mp3'),
    attribution: ATTRIBUTION,
  },
  {
    id: "rossini-largo-factotum",
    composer: "Gioachino Rossini",
    composerYears: "1792–1868",
    title: "Largo al Factotum (The Barber of Seville)",
    composedYear: "1816",
    composedYearN: 1816,
    era: "romantic",
    glyph: "✂️",
    history:
      "Rossini wrote The Barber of Seville in just three weeks when he was 23. This rapid-fire aria is the barber Figaro introducing himself — bragging, cheerfully, that everyone in town needs his services. It's one of the hardest tongue-twisters in all of opera.",
    audioUrl: url("11 Largo al Factotum from The Barber of Seville.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "schubert-marche-militaire",
    composer: "Franz Schubert",
    composerYears: "1797–1828",
    title: "Marche Militaire",
    composedYear: "1818",
    composedYearN: 1818,
    era: "romantic",
    glyph: "🎖️",
    history:
      "Schubert wrote this jaunty march for two friends to play together at one piano — four hands on the keyboard. He wrote over six hundred songs and a dozen symphonies in his short life of just thirty-one years.",
    audioUrl: url("12 Marche Militaire.mp3"),
    attribution: ATTRIBUTION,
  },

  // ─── Romantic ─────────────────────────────────────────────────────────
  {
    id: "paganini-moto-perpetuo",
    composer: "Niccolò Paganini",
    composerYears: "1782–1840",
    title: "Moto Perpetuo, Op. 11",
    composedYear: "c. 1830",
    composedYearN: 1830,
    era: "romantic",
    glyph: "🎻",
    history:
      "Paganini was such an astonishing violinist that people whispered he must have made a deal with the devil. 'Moto perpetuo' means 'perpetual motion' — the piece is an unbroken stream of notes that never stops to breathe.",
    audioUrl: url("08 Moto Perpetuo, Op. 11.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "bellini-casta-diva",
    composer: "Vincenzo Bellini",
    composerYears: "1801–1835",
    title: "Casta Diva (from Norma)",
    composedYear: "1831",
    composedYearN: 1831,
    era: "romantic",
    glyph: "🌙",
    history:
      "Casta Diva — 'Chaste Goddess' — is a Druid priestess's prayer to the moon. Bellini's flowing melody floats above the orchestra like the moon itself rising. Bellini died at thirty-three, having written ten operas.",
    audioUrl: url("09 Casta Diva from Norma.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "schumann-traumerei",
    composer: "Robert Schumann",
    composerYears: "1810–1856",
    title: "Träumerei (Scenes from Childhood)",
    composedYear: "1838",
    composedYearN: 1838,
    era: "romantic",
    glyph: "☁️",
    history:
      "Schumann wrote thirteen short piano pieces called Kinderszenen — Scenes from Childhood — looking back at the wonder of being small. 'Träumerei' means 'Dreaming,' and this gentle melody is one of the most beloved miniatures ever written.",
    audioUrl: url("16 Traumerei.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "chopin-military-polonaise",
    composer: "Frédéric Chopin",
    composerYears: "1810–1849",
    title: "Military Polonaise (Op. 40 No. 1)",
    composedYear: "1838",
    composedYearN: 1838,
    era: "romantic",
    glyph: "🎖️",
    history:
      "Chopin was Polish but spent most of his life in Paris, longing for his homeland. The polonaise is a stately Polish dance, and this proud, march-like one became a symbol of Polish courage during years of foreign rule.",
    audioUrl: url("14 Military Polonaise.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "mendelssohn-midsummer-scherzo",
    composer: "Felix Mendelssohn",
    composerYears: "1809–1847",
    title: "Scherzo (A Midsummer Night's Dream)",
    composedYear: "1842",
    composedYearN: 1842,
    era: "romantic",
    glyph: "🧚",
    history:
      "Mendelssohn first read Shakespeare's Midsummer Night's Dream when he was seventeen. The Scherzo is the music of the play's fairies — quicksilver, whispering, like wings beating in moonlight. He was a child prodigy who could play any Bach fugue from memory.",
    audioUrl: url("17 Scherzo from A Midsummer Night's Dream.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "berlioz-rakoczy-march",
    composer: "Hector Berlioz",
    composerYears: "1803–1869",
    title: "Rákóczy March (The Damnation of Faust)",
    composedYear: "1846",
    composedYearN: 1846,
    era: "romantic",
    glyph: "🥁",
    history:
      "Berlioz heard this fiery Hungarian march while visiting Pest in 1846 and quickly orchestrated his own brilliant version. The audience reaction was so wild that he later worked it into his Damnation of Faust.",
    audioUrl: url("07 Rakoczy March from the Damnation of Faust.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "strauss-radetzky-march",
    composer: "Johann Strauss Sr.",
    composerYears: "1804–1849",
    title: "Radetzky March",
    composedYear: "1848",
    composedYearN: 1848,
    era: "romantic",
    glyph: "🎺",
    history:
      "The elder Johann Strauss wrote this in honour of an Austrian field marshal. Today it's the cheerful encore that ends the Vienna Philharmonic's New Year's Concert every January, with the audience clapping along to the chorus.",
    audioUrl: url("10 Radetzky March.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "liszt-liebestraum",
    composer: "Franz Liszt",
    composerYears: "1811–1886",
    title: "Liebestraum No. 3",
    composedYear: "1850",
    composedYearN: 1850,
    era: "romantic",
    glyph: "❤️",
    history:
      "'Liebestraum' means 'Dream of Love.' Liszt was the first piano superstar — women fainted at his concerts, and the term 'Lisztomania' was coined to describe the frenzy. Late in life he became a Catholic priest.",
    audioUrl: url("15 Liebestraum.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "rubinstein-melody-f",
    composer: "Anton Rubinstein",
    composerYears: "1829–1894",
    title: "Melody in F (Op. 3 No. 1)",
    composedYear: "1852",
    composedYearN: 1852,
    era: "romantic",
    glyph: "🎹",
    history:
      "Rubinstein was a Russian pianist often compared to Liszt. He wrote this simple, singing melody when he was just twenty-three, and it became one of the most famous piano tunes of the entire 19th century.",
    audioUrl: url("25 Melody in F, Op. 3, No. 1.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "strauss-blue-danube",
    composer: "Johann Strauss II",
    composerYears: "1825–1899",
    title: "On the Beautiful Blue Danube",
    composedYear: "1866",
    composedYearN: 1866,
    era: "romantic",
    glyph: "🌊",
    history:
      "The 'Waltz King' of Vienna wrote over four hundred waltzes, polkas, and marches. The Blue Danube is the unofficial second national anthem of Austria, painting in music a lazy summer's float down Europe's great river.",
    audioUrl: url("22 On the Beautiful Blue Danube.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "smetana-bartered-bride",
    composer: "Bedřich Smetana",
    composerYears: "1824–1884",
    title: "Dance of the Comedians (The Bartered Bride)",
    composedYear: "1866",
    composedYearN: 1866,
    era: "romantic",
    glyph: "🎪",
    history:
      "Smetana is the founder of Czech national music. The Bartered Bride is a comic opera set in a village where a travelling circus arrives — and these are the tumblers and dancers warming up the crowd.",
    audioUrl: url("27 Dance of the Comedians from 'The Bartered Bride'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "suppe-light-cavalry",
    composer: "Franz von Suppé",
    composerYears: "1819–1895",
    title: "Light Cavalry Overture",
    composedYear: "1866",
    composedYearN: 1866,
    era: "romantic",
    glyph: "🐎",
    history:
      "Suppé was an Austrian operetta composer. The galloping fanfare from this overture has been used in countless cartoons and Westerns since the invention of film — so familiar that even people who've never been to an opera will recognize it.",
    audioUrl: url("24 Light Cavalry Overture.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "brahms-hungarian-5",
    composer: "Johannes Brahms",
    composerYears: "1833–1897",
    title: "Hungarian Dance No. 5",
    composedYear: "1869",
    composedYearN: 1869,
    era: "romantic",
    glyph: "💃",
    history:
      "Brahms loved the wild gypsy music he heard in Hungarian taverns and arranged twenty-one Hungarian Dances for piano. No. 5 — fiery, sudden, full of stops and starts — is the most famous of them all.",
    audioUrl: url("26 Hungarian Dance No. 5.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "delibes-coppelia-waltz",
    composer: "Léo Delibes",
    composerYears: "1836–1891",
    title: "Waltz from Coppélia",
    composedYear: "1870",
    composedYearN: 1870,
    era: "romantic",
    glyph: "🪆",
    history:
      "Coppélia is a ballet about a clever toymaker and a life-sized dancing doll. Delibes brought a new wit and elegance to ballet music; Tchaikovsky studied his scores carefully before writing Swan Lake.",
    audioUrl: url("28 Waltz from Coppelia.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "wagner-valkyries",
    composer: "Richard Wagner",
    composerYears: "1813–1883",
    title: "Ride of the Valkyries (Die Walküre)",
    composedYear: "1870",
    composedYearN: 1870,
    era: "romantic",
    glyph: "⚔️",
    history:
      "In Norse mythology, the Valkyries are warrior maidens who ride through the sky carrying fallen heroes to Valhalla. Wagner's thundering music has become the very sound of charging horsemen in books, films, and parades.",
    audioUrl: url("19 Ride of the Valkyries from Die Walkure.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "verdi-aida-march",
    composer: "Giuseppe Verdi",
    composerYears: "1813–1901",
    title: "Triumphal March from Aida",
    composedYear: "1871",
    composedYearN: 1871,
    era: "romantic",
    glyph: "🐘",
    history:
      "Verdi composed Aida for the new opera house in Cairo. The Triumphal March accompanies the Egyptian army returning home in glory — and on real opera stages it is sometimes paraded with horses, camels, and (very rarely) live elephants.",
    audioUrl: url("18 Triumphal March from Aida.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "franck-panis-angelicus",
    composer: "César Franck",
    composerYears: "1822–1890",
    title: "Panis Angelicus",
    composedYear: "1872",
    composedYearN: 1872,
    era: "romantic",
    glyph: "🕊️",
    history:
      "'Panis Angelicus' — the Bread of Angels — is a hymn by St. Thomas Aquinas about the Eucharist. Franck was a quiet, devout church organist in Paris; this serene setting is one of the most beloved sacred melodies of the 19th century.",
    audioUrl: url("29 Panis Angelicus.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "mussorgsky-great-gate-kiev",
    composer: "Modest Mussorgsky",
    composerYears: "1839–1881",
    title: "The Great Gate at Kiev (Pictures at an Exhibition)",
    composedYear: "1874",
    composedYearN: 1874,
    era: "romantic",
    glyph: "🏰",
    history:
      "Mussorgsky composed Pictures at an Exhibition after walking through a memorial show of his late friend's paintings. The grand finale imagines a never-built ceremonial gate for the city of Kiev — vast, golden, bells ringing.",
    audioUrl: url("33 The Great Gate at Kiev from 'Pictures at an Exhibition'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "bizet-habanera",
    composer: "Georges Bizet",
    composerYears: "1838–1875",
    title: "Habanera (Carmen)",
    composedYear: "1875",
    composedYearN: 1875,
    era: "romantic",
    glyph: "🌹",
    history:
      "Bizet's Carmen was hissed at its 1875 premiere — too modern, too daring. He died of a heart attack three months later, never knowing it would become one of the most beloved operas in the world. The Habanera is Carmen's sultry entrance song.",
    audioUrl: url("32 Habanera from 'Carmen'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "grieg-solveigs-song",
    composer: "Edvard Grieg",
    composerYears: "1843–1907",
    title: "Solveig's Song (Peer Gynt)",
    composedYear: "1875",
    composedYearN: 1875,
    era: "romantic",
    glyph: "🌲",
    history:
      "Grieg was Norway's national composer. Solveig is the patient woman waiting through long Nordic winters for her wandering Peer to come home; this is the lullaby she sings to herself, year after year.",
    audioUrl: url("38 Solveig's Song from Peer Gynt.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "goldmark-queen-sheba",
    composer: "Karl Goldmark",
    composerYears: "1830–1915",
    title: "Grand March from The Queen of Sheba",
    composedYear: "1875",
    composedYearN: 1875,
    era: "romantic",
    glyph: "👑",
    history:
      "A second Queen of Sheba on this album — Goldmark's Hungarian opera tells the same Old Testament story Handel set 130 years earlier. This stately march is the queen's grand procession into Solomon's court.",
    audioUrl: url("21 Grand March from 'The Queen of Sheba'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "borodin-quartet-scherzo",
    composer: "Alexander Borodin",
    composerYears: "1833–1887",
    title: "Scherzo (String Quartet No. 2)",
    composedYear: "1881",
    composedYearN: 1881,
    era: "romantic",
    glyph: "🎻",
    history:
      "Borodin was a chemist by day — he taught medical students and discovered new chemical reactions. Music was his hobby, written on weekends. This sparkling Scherzo bubbles like one of his own laboratory experiments.",
    audioUrl: url("23 Scherzo from 'String Quartet No. 2'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "offenbach-barcarolle",
    composer: "Jacques Offenbach",
    composerYears: "1819–1880",
    title: "Barcarolle (The Tales of Hoffmann)",
    composedYear: "1881",
    composedYearN: 1881,
    era: "romantic",
    glyph: "🚣",
    history:
      "A barcarolle is a song of the Venetian gondoliers, rocking gently like a boat on a canal. Offenbach died before finishing his opera, but this duet — sung in moonlight on a Venetian gondola — has floated on, beloved.",
    audioUrl: url("20 Barcarolle from The 'Tales of Hoffmann'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "waldteufel-skaters-waltz",
    composer: "Émile Waldteufel",
    composerYears: "1837–1915",
    title: "Les Patineurs (The Skaters' Waltz)",
    composedYear: "1882",
    composedYearN: 1882,
    era: "romantic",
    glyph: "⛸️",
    history:
      "Waldteufel was the Strauss of France. This waltz pictures Parisians gliding around a frozen lake on winter Sundays — a long French tradition that gave us the very word 'rink.'",
    audioUrl: url("30 The Skaters Waltz.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "chabrier-espana",
    composer: "Emmanuel Chabrier",
    composerYears: "1841–1894",
    title: "España Rhapsody",
    composedYear: "1883",
    composedYearN: 1883,
    era: "romantic",
    glyph: "💃",
    history:
      "Chabrier traveled through Spain in 1882, jotting down folk tunes and dance rhythms. The result was this dazzling rhapsody, full of clapping, stamping, and all the fire of a Spanish village dance under hot summer stars.",
    audioUrl: url("35 Espana Rhapsody.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "saint-saens-swan",
    composer: "Camille Saint-Saëns",
    composerYears: "1835–1921",
    title: "The Swan (The Carnival of the Animals)",
    composedYear: "1886",
    composedYearN: 1886,
    era: "romantic",
    glyph: "🦢",
    history:
      "Saint-Saëns wrote The Carnival of the Animals as a private joke for friends, with funny musical portraits of lions, hens, and tortoises. He was so embarrassed by it he forbade publication during his lifetime — except for The Swan, which he loved.",
    audioUrl: url("31 The Swan from 'Carnival of the Animals'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "rimsky-scheherazade",
    composer: "Nikolai Rimsky-Korsakov",
    composerYears: "1844–1908",
    title: "The Sea and Sinbad's Ship (Scheherazade)",
    composedYear: "1888",
    composedYearN: 1888,
    era: "romantic",
    glyph: "🌊",
    history:
      "In One Thousand and One Nights, Scheherazade tells the sultan a story every evening to delay her execution. Rimsky-Korsakov turned four of her tales into a glittering symphonic suite — this opening movement paints Sinbad's ship rocking on the open sea.",
    audioUrl: url("45 'The Sea and Sinbad's Ship' Theme from Scheherazade.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "debussy-reverie",
    composer: "Claude Debussy",
    composerYears: "1862–1918",
    title: "Rêverie",
    composedYear: "1890",
    composedYearN: 1890,
    era: "romantic",
    glyph: "☁️",
    history:
      "Debussy invented Impressionism in music — sounds that drift and shimmer rather than march in straight lines. 'Rêverie' simply means 'daydream,' and this gentle piano piece is exactly that: a few minutes of letting the mind wander.",
    audioUrl: url("37 Reverie.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "albeniz-tango-d",
    composer: "Isaac Albéniz",
    composerYears: "1860–1909",
    title: "Tango in D",
    composedYear: "1890",
    composedYearN: 1890,
    era: "romantic",
    glyph: "🌹",
    history:
      "Albéniz was a Spanish piano prodigy who ran away from home as a child to perform around the world. This early tango — the slow, swaying dance from Argentina — became one of his most beloved miniatures.",
    audioUrl: url("44 Tango in D.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "mascagni-intermezzo",
    composer: "Pietro Mascagni",
    composerYears: "1863–1945",
    title: "Intermezzo (Cavalleria Rusticana)",
    composedYear: "1890",
    composedYearN: 1890,
    era: "romantic",
    glyph: "⛪",
    history:
      "An intermezzo is a quiet musical pause between dramatic scenes of an opera. This one — set on Easter morning in a Sicilian village — is so tender that it has soundtracked countless films, weddings, and funerals.",
    audioUrl: url("40 Intermezzo from Cavalleria Rusticana.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "tchaikovsky-sugar-plum",
    composer: "Pyotr Ilyich Tchaikovsky",
    composerYears: "1840–1893",
    title: "Dance of the Sugar Plum Fairy (The Nutcracker)",
    composedYear: "1892",
    composedYearN: 1892,
    era: "romantic",
    glyph: "🍭",
    history:
      "For the celesta — a brand-new instrument that sounds like a music box — Tchaikovsky wrote this glittering dance for the queen of the Land of Sweets. The Nutcracker is now performed every Christmas in cities around the world.",
    audioUrl: url("34 Dance of the Sugar Plum Fairy from 'The Nutcracker Suite'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "dvorak-humoresque",
    composer: "Antonín Dvořák",
    composerYears: "1841–1904",
    title: "Humoresque (Op. 101 No. 7)",
    composedYear: "1894",
    composedYearN: 1894,
    era: "romantic",
    glyph: "😄",
    history:
      "Dvořák was a Czech composer who spent three years teaching in New York City, where he wrote this gently joking little tune. A 'humoresque' is a piece with a hint of a smile in it.",
    audioUrl: url("36 Humoresque.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "faure-berceuse",
    composer: "Gabriel Fauré",
    composerYears: "1845–1924",
    title: "Berceuse (Dolly Suite)",
    composedYear: "1894",
    composedYearN: 1894,
    era: "romantic",
    glyph: "👶",
    history:
      "A 'berceuse' is a French lullaby. Fauré wrote the Dolly Suite — six pieces for piano four-hands — as birthday gifts for the young daughter of a friend. He was the gentle teacher of an entire generation of French composers.",
    audioUrl: url("39 Berceuse from Dolly.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "massenet-meditation-thais",
    composer: "Jules Massenet",
    composerYears: "1842–1912",
    title: "Méditation (Thaïs)",
    composedYear: "1894",
    composedYearN: 1894,
    era: "romantic",
    glyph: "🕊️",
    history:
      "In Massenet's opera Thaïs, an Egyptian woman pauses to consider whether to leave her old life and follow God. This solo violin Méditation is the music of her heart making up its mind — patient, searching, transformed.",
    audioUrl: url("47 Meditation from Thais.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "macdowell-wild-rose",
    composer: "Edward MacDowell",
    composerYears: "1860–1908",
    title: "To a Wild Rose (Op. 51 No. 1)",
    composedYear: "1896",
    composedYearN: 1896,
    era: "romantic",
    glyph: "🌹",
    history:
      "MacDowell was the first great American classical composer. He wrote this brief, unaffected miniature at his summer farm in New Hampshire, watching wild roses grow at the edge of the woods. It became the most famous American piano piece of its era.",
    audioUrl: url("41 To A Wild Rose.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "sousa-stars-stripes",
    composer: "John Philip Sousa",
    composerYears: "1854–1932",
    title: "The Stars and Stripes Forever",
    composedYear: "1896",
    composedYearN: 1896,
    era: "romantic",
    glyph: "🇺🇸",
    history:
      "Sousa was bandmaster of the United States Marine Band and the most popular composer of marches who ever lived. He wrote 'Stars and Stripes' on Christmas Day, 1896. Congress later made it the official march of the United States.",
    audioUrl: url("42 The Stars and Stripes Forever.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "strauss-zarathustra",
    composer: "Richard Strauss",
    composerYears: "1864–1949",
    title: "Also Sprach Zarathustra (excerpt)",
    composedYear: "1896",
    composedYearN: 1896,
    era: "romantic",
    glyph: "☀️",
    history:
      "Strauss imagined the dawn of the world: a low rumble, a single trumpet call, an immense chord blooming as the sun rises. Most listeners now know this opening from the film 2001: A Space Odyssey.",
    audioUrl: url("46 Also Sprach Zarathustra (excerpt).mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "ravel-pavane",
    composer: "Maurice Ravel",
    composerYears: "1875–1937",
    title: "Pavane for a Dead Princess (excerpt)",
    composedYear: "1899",
    composedYearN: 1899,
    era: "romantic",
    glyph: "👑",
    history:
      "Ravel was a French composer of marvelous precision — every note exactly placed. The Pavane is a slow, ceremonial dance from old Spain; he imagined it being performed at the court of a long-ago Spanish princess. He insisted the music was not actually sad.",
    audioUrl: url("56 Pavane for a Dead Princess (excerpt).mp3"),
    attribution: ATTRIBUTION,
  },

  // ─── Modern (20th c.) ────────────────────────────────────────────────
  {
    id: "elgar-pomp-circumstance",
    composer: "Edward Elgar",
    composerYears: "1857–1934",
    title: "Pomp and Circumstance March No. 1",
    composedYear: "1901",
    composedYearN: 1901,
    era: "modern",
    glyph: "🎓",
    history:
      "Elgar was a self-taught English composer. King Edward VII heard the soaring tune in this march and suggested adding words to it; the result, 'Land of Hope and Glory,' became Britain's unofficial second anthem. Now it is the music of graduations.",
    audioUrl: url("48 Pomp and Circumstance March No. 1.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "sibelius-valse-triste",
    composer: "Jean Sibelius",
    composerYears: "1865–1957",
    title: "Valse Triste",
    composedYear: "1903",
    composedYearN: 1903,
    era: "modern",
    glyph: "🌹",
    history:
      "Sibelius was Finland's greatest composer — his Finlandia became a symbol of the country's independence. Valse Triste — 'Sad Waltz' — was written for a play, depicting a dying woman who imagines herself dancing one last waltz with strangers.",
    audioUrl: url("49 Valse Triste.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "alfven-swedish-rhapsody",
    composer: "Hugo Alfvén",
    composerYears: "1872–1960",
    title: "Swedish Rhapsody (Midsommarvaka)",
    composedYear: "1903",
    composedYearN: 1903,
    era: "modern",
    glyph: "🌻",
    history:
      "Alfvén stitched together folk songs he had collected on midsummer-night festivals in the Swedish countryside, when villagers stay up under the long bright Nordic sky. The piece is sometimes called Sweden's unofficial national portrait.",
    audioUrl: url("51 Swedish Rhapsody.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "lehar-vilia",
    composer: "Franz Lehár",
    composerYears: "1870–1948",
    title: "Vilia (The Merry Widow)",
    composedYear: "1905",
    composedYearN: 1905,
    era: "modern",
    glyph: "🌲",
    history:
      "Vilia is a fairy of the woods in Slavic folklore. In Lehár's Viennese operetta The Merry Widow, the heroine sings this aching song about a hunter who falls in love with a Vilia and is then left forever longing for her.",
    audioUrl: url("60 Vilia from The Merry Widow.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "stravinsky-fireworks",
    composer: "Igor Stravinsky",
    composerYears: "1882–1971",
    title: "Fireworks (Op. 4)",
    composedYear: "1908",
    composedYearN: 1908,
    era: "modern",
    glyph: "🎆",
    history:
      "Stravinsky wrote Fireworks at twenty-six, as a wedding gift for his teacher's daughter. Sergei Diaghilev heard it, was electrified, and commissioned the young composer to write ballets — beginning with The Firebird and Petrushka.",
    audioUrl: url("62 Fireworks.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "kreisler-liebesfreud",
    composer: "Fritz Kreisler",
    composerYears: "1875–1962",
    title: "Liebesfreud",
    composedYear: "1910",
    composedYearN: 1910,
    era: "modern",
    glyph: "💝",
    history:
      "Kreisler was an Austrian violinist of legendary warmth and charm. 'Liebesfreud' means 'Love's Joy,' the cheerful sister to his more famous Liebesleid ('Love's Sorrow'). He liked to play encores he had written himself, in old-Vienna style.",
    audioUrl: url("55 Liebesfreud.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "granados-maiden-nightingale",
    composer: "Enrique Granados",
    composerYears: "1867–1916",
    title: "The Maiden and the Nightingale (Goyescas)",
    composedYear: "1911",
    composedYearN: 1911,
    era: "modern",
    glyph: "🌹",
    history:
      "Granados was inspired by the paintings of Goya. This nocturne pictures a girl in a moonlit Madrid garden, listening to a nightingale answer her thoughts. Granados drowned in 1916 when his ship was torpedoed on the way home from his New York opera premiere.",
    audioUrl: url("54 The Maiden and the Nightingale from Goyescas.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "falla-ritual-fire-dance",
    composer: "Manuel de Falla",
    composerYears: "1876–1946",
    title: "Ritual Fire Dance (El Amor Brujo)",
    composedYear: "1915",
    composedYearN: 1915,
    era: "modern",
    glyph: "🔥",
    history:
      "El Amor Brujo — 'Love, the Magician' — is a ballet about a Spanish gypsy widow trying to break free of her dead husband's haunting ghost. This blazing dance is the midnight ritual she performs around the fire, summoning courage to live again.",
    audioUrl: url("57 Ritual Fire Dance from El Amor Brujo.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "puccini-o-mio-babbino",
    composer: "Giacomo Puccini",
    composerYears: "1858–1924",
    title: "O Mio Babbino Caro (Gianni Schicchi)",
    composedYear: "1918",
    composedYearN: 1918,
    era: "modern",
    glyph: "💌",
    history:
      "'Oh My Beloved Father' — a young woman pleading with her father to let her marry the boy she loves. The aria lasts barely two minutes, but it's one of the most heart-stoppingly beautiful melodies Puccini ever wrote.",
    audioUrl: url("43 O Mio Babbino Caro (Oh, My Beloved Father) from 'Gianni Schicchi'.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "respighi-pines-rome",
    composer: "Ottorino Respighi",
    composerYears: "1879–1936",
    title: "Pines of the Appian Way",
    composedYear: "1924",
    composedYearN: 1924,
    era: "modern",
    glyph: "🌲",
    history:
      "Respighi imagined a Roman legion returning to the Eternal City along the ancient Appian Way at dawn. The music starts as a distant murmur and grows into a thundering brass procession marching toward the listener — twenty centuries of footsteps.",
    audioUrl: url("58 The Pines of the Appian Way from The Pines of Rome.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "gershwin-rhapsody-blue",
    composer: "George Gershwin",
    composerYears: "1898–1937",
    title: "Rhapsody in Blue",
    composedYear: "1924",
    composedYearN: 1924,
    era: "modern",
    glyph: "🌃",
    history:
      "Gershwin composed Rhapsody in Blue in three weeks for a 1924 New York concert advertised as 'An Experiment in Modern Music.' He fused jazz and classical music into one shimmering, swaggering portrait of the city — beginning with one unforgettable clarinet wail.",
    audioUrl: url("64 Rhapsody in Blue.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "villa-lobos-little-train",
    composer: "Heitor Villa-Lobos",
    composerYears: "1887–1959",
    title: "The Little Train of the Brazilian Countryman",
    composedYear: "1930",
    composedYearN: 1930,
    era: "modern",
    glyph: "🚂",
    history:
      "Villa-Lobos was Brazil's greatest composer. This piece is a musical train ride: you can hear the engine starting up, the wheels picking up speed, the steam whistle, and the train pulling slowly into a country station as the music settles.",
    audioUrl: url("63 The Little Train of the Brazilian Countryman.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "grofe-on-the-trail",
    composer: "Ferde Grofé",
    composerYears: "1892–1972",
    title: "On the Trail (Grand Canyon Suite)",
    composedYear: "1931",
    composedYearN: 1931,
    era: "modern",
    glyph: "🏜️",
    history:
      "Grofé pictured a clip-clopping mule train descending into the Grand Canyon — the violin solo is the cowboy whistling, the clopping rhythm is the mules. He was Gershwin's orchestrator and the first great composer to paint American landscapes in symphonic music.",
    audioUrl: url("67 On the Trail from Grand Canyon Suite.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "coates-knightsbridge",
    composer: "Eric Coates",
    composerYears: "1886–1957",
    title: "Knightsbridge March (London Suite)",
    composedYear: "1933",
    composedYearN: 1933,
    era: "modern",
    glyph: "🏛️",
    history:
      "Coates wrote charming, brisk English light music. His Knightsbridge March pictures a busy London neighbourhood — and was used for over twenty years as the theme of BBC radio's In Town Tonight, making it one of the best-known tunes in Britain.",
    audioUrl: url("50 Knightsbridge March from London Suite.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "rachmaninoff-paganini-18",
    composer: "Sergei Rachmaninoff",
    composerYears: "1873–1943",
    title: "Rhapsody on a Theme by Paganini, 18th Variation",
    composedYear: "1934",
    composedYearN: 1934,
    era: "modern",
    glyph: "🎹",
    history:
      "Rachmaninoff took a famous violin theme by Paganini and wrote twenty-four variations on it. In the 18th, he simply turned the original tune upside-down — and out came one of the most ravishing love melodies ever written.",
    audioUrl: url("53 Rhapsody on a Theme by Paganini, 18th Variation.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "vaughan-williams-greensleeves",
    composer: "Ralph Vaughan Williams",
    composerYears: "1872–1958",
    title: "Fantasia on Greensleeves",
    composedYear: "1934",
    composedYearN: 1934,
    era: "modern",
    glyph: "🌿",
    history:
      "Greensleeves is one of the oldest English melodies — sung in Shakespeare's day, possibly even earlier. Vaughan Williams loved old folk songs and wrapped this one in soft, glowing strings, like a green hill seen through morning mist.",
    audioUrl: url("52 Fantasia on Greensleeves.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "prokofiev-troika",
    composer: "Sergei Prokofiev",
    composerYears: "1891–1953",
    title: "Troika (Lieutenant Kijé Suite)",
    composedYear: "1934",
    composedYearN: 1934,
    era: "modern",
    glyph: "🛷",
    history:
      "A troika is a Russian sleigh pulled by three horses. Prokofiev wrote this jingling, snowy ride for a film about an imaginary military officer who never existed. The bells in the orchestra are real sleigh bells.",
    audioUrl: url("59 Troika from Lieutenant Kije Suite.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "khachaturian-sabre-dance",
    composer: "Aram Khachaturian",
    composerYears: "1903–1978",
    title: "Sabre Dance (Gayane)",
    composedYear: "1942",
    composedYearN: 1942,
    era: "modern",
    glyph: "⚔️",
    history:
      "Khachaturian was an Armenian composer; his Sabre Dance comes from a ballet about a brave young woman on a Soviet collective farm. The frantic main tune is a real Armenian war dance, played as warriors brandished their swords overhead.",
    audioUrl: url("61 Sabre Dance from Gayne.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "copland-simple-gifts",
    composer: "Aaron Copland",
    composerYears: "1900–1990",
    title: "Simple Gifts (Appalachian Spring)",
    composedYear: "1944",
    composedYearN: 1944,
    era: "modern",
    glyph: "🌾",
    history:
      "Copland wove the Shaker hymn 'Simple Gifts' into his ballet about a young pioneer couple building their first farmhouse in the Pennsylvania hills. The hymn's words: 'Tis a gift to be simple, 'tis a gift to be free.' Copland is often called the composer of the American sound.",
    audioUrl: url("70 Simple Gifts from Appalachian Spring.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "anderson-sleigh-ride",
    composer: "Leroy Anderson",
    composerYears: "1908–1975",
    title: "Sleigh Ride",
    composedYear: "1948",
    composedYearN: 1948,
    era: "modern",
    glyph: "🎄",
    history:
      "Anderson started writing Sleigh Ride during a heat wave in Connecticut, dreaming of cold weather. The trumpet at the end imitates a horse's whinny; the orchestra includes real sleigh bells and a slap-stick that sounds like a whip. It became a Christmas standard the world over.",
    audioUrl: url("68 Sleigh Ride.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "shostakovich-romance-gadfly",
    composer: "Dmitri Shostakovich",
    composerYears: "1906–1975",
    title: "Romance (The Gadfly)",
    composedYear: "1955",
    composedYearN: 1955,
    era: "modern",
    glyph: "🌹",
    history:
      "Shostakovich wrote film music to support his family while composing his great symphonies on the side. This soaring violin Romance, from a film about an Italian revolutionary, is one of the most tender melodies of the entire 20th century.",
    audioUrl: url("65 Romance from the Gadfly.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "bernstein-candide-overture",
    composer: "Leonard Bernstein",
    composerYears: "1918–1990",
    title: "Overture from Candide",
    composedYear: "1956",
    composedYearN: 1956,
    era: "modern",
    glyph: "🎭",
    history:
      "Bernstein was the dazzling conductor of the New York Philharmonic and the composer of West Side Story. The overture to his comic operetta Candide tumbles, races, and laughs through four minutes — a brilliant musical somersault.",
    audioUrl: url("66 Overture from Candide.mp3"),
    attribution: ATTRIBUTION,
  },
  {
    id: "tropical",
    composer: "Various",
    composerYears: "20th c.",
    title: "Tropical",
    composedYear: "20th c.",
    composedYearN: 1950,
    era: "modern",
    glyph: "🌴",
    history:
      "A short 20th-century light-music miniature included in this curated album.",
    audioUrl: url("69 Tropical.mp3"),
    attribution: ATTRIBUTION,
  },
];

// Sort chronologically so the rotation walks the family through music history.
MUSIC_TRACKS.sort((a, b) => a.composedYearN - b.composedYearN);

export const MUSIC_TRACKS_BY_ID: Record<string, MusicTrack> = Object.fromEntries(
  MUSIC_TRACKS.map((t) => [t.id, t]),
);

export const PLAYABLE_TRACKS = MUSIC_TRACKS.filter((t) => t.audioUrl !== null);
